/**
 * CreateDMModal - Create DM: user search + select.
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { chatChannelsApi } from '../../services/chatApi';
import { useChat } from '../../contexts/ChatContext';

interface CreateDMModalProps {
  onClose: () => void;
}

const CreateDMModal: React.FC<CreateDMModalProps> = ({ onClose }) => {
  const { t } = useTranslation('chat');
  const { setCurrentChannel, refreshChannels } = useChat();
  const [userId, setUserId] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = useCallback(async () => {
    const uid = parseInt(userId, 10);
    if (isNaN(uid)) return;

    setCreating(true);
    try {
      const channel = await chatChannelsApi.createDM(uid);
      await refreshChannels();
      setCurrentChannel(channel);
      onClose();
    } catch (e) {
      console.error('Failed to create DM:', e);
    } finally {
      setCreating(false);
    }
  }, [userId, setCurrentChannel, refreshChannels, onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 text-gray-900">
        <h2 className="text-lg font-semibold mb-4">{t('newDirectMessage')}</h2>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('userId')}</label>
          <input
            type="number"
            value={userId}
            onChange={e => setUserId(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('enterUserId')}
          />
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">{t('cancel')}</button>
          <button
            onClick={handleCreate}
            disabled={!userId || creating}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {creating ? t('creating') : t('startChat')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateDMModal;
